import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import { createItem, getItem, imageUrl, updateItem, uploadImages } from '../api';
import { useAuth } from '../auth';
import JewelleryMetalNotice from '../components/JewelleryMetalNotice';
import FormWizard from '../components/FormWizard';
import ListingExtraFields from '../components/ListingExtraFields';
import JewellerySpecFields, {
  JEWELLERY_CATEGORIES, jewellerySpecDefaults, jewellerySpecFromItem, jewellerySpecToAttributes
} from '../components/JewellerySpecFields';
import BlockedDatesFields, { sanitizeBlockedDates } from '../components/BlockedDatesFields';
import InventoryPanel from '../components/InventoryPanel';
import { saveBlockedDates } from '../api';

// Caps how many photos one listing can carry in the gallery - the upload endpoint itself also
// caps a single upload request at 6 files (routes/uploads.js), this is the total across
// multiple uploads for one listing.
const MAX_IMAGES = 8;

// fix_issue3.md #5 - Cash-On-Delivery can only be offered on items renting at or below this
// per-day rate (mirrors rentalapi's pricingConfig.codMaxDayRate).
const COD_MAX_DAY_RATE = 500;

const emptyPolicies = { securityDeposit: false, cashOnDelivery: false, freeShipping: false, freeMaintenance: false, minBookingAmount: 0, minBookingPercent: 0 };

const emptyForm = {
  name: '',
  description: '',
  price: '',
  priceUnit: 'week',
  stock: '',
  category: 'Necklace Set',
  location: '',
  images: [],
  ...jewellerySpecDefaults,
  specifications: [],
  policies: { ...emptyPolicies },
  blockedDates: []
};

export function toFormState(item) {
  return {
    name: item.name || '',
    description: item.description || '',
    price: item.price ?? '',
    priceUnit: item.priceUnit || 'week',
    stock: item.stock ?? 1,
    category: item.category || 'Necklace Set',
    location: item.location || '',
    images: item.images || [],
    ...jewellerySpecFromItem(item),
    specifications: item.specifications?.length ? item.specifications.map((s) => ({ label: s.label || '', value: s.value || '' })) : [],
    policies: { ...emptyPolicies, ...(item.policies || {}) },
    blockedDates: Array.isArray(item.blockedDates)
      ? item.blockedDates.map((b) => ({
        from: b.from ? String(b.from).slice(0, 10) : '',
        to: b.to ? String(b.to).slice(0, 10) : '',
        kind: b.kind || 'other',
        reason: b.reason || ''
      }))
      : []
  };
}

export function toPayload(form) {
  return {
    name: form.name,
    description: form.description,
    price: Number(form.price),
    priceUnit: form.priceUnit,
    stock: Number(form.stock),
    category: form.category,
    location: form.location,
    images: form.images,
    attributes: jewellerySpecToAttributes(form),
    specifications: form.specifications.filter((s) => s.label || s.value),
    // v6: the policy/min-payment UI now comes from the shared <ListingExtraFields>, which can seed
    // extra (car-only) keys onto form.policies - so the payload is assembled from the known six
    // fields explicitly rather than spreading, keeping the sent shape stable.
    policies: {
      securityDeposit: !!form.policies.securityDeposit,
      // COD is invalid above the cap - never send it enabled even if the box was left ticked
      // before the price was raised.
      cashOnDelivery: !!form.policies.cashOnDelivery && Number(form.price) > 0 && Number(form.price) <= COD_MAX_DAY_RATE,
      freeShipping: !!form.policies.freeShipping,
      freeMaintenance: !!form.policies.freeMaintenance,
      // v4_fix_issues #1/#3 - only one of the two minimum-payment fields is ever non-zero.
      minBookingAmount: Number(form.policies.minBookingAmount) > 0 ? Number(form.policies.minBookingAmount) : 0,
      minBookingPercent: Number(form.policies.minBookingAmount) > 0 ? 0 : (Number(form.policies.minBookingPercent) || 0)
    }
  };
}

function ListingPreview({ form }) {
  return (
    <Card>
      {form.images?.[0] && (
        <Card.Img variant="top" src={imageUrl(form.images[0])} style={{ height: 200, objectFit: 'cover' }} alt={form.name} />
      )}
      <Card.Body>
        <Card.Title>{form.name || 'Untitled piece'}</Card.Title>
        <div className="text-muted mb-2">{form.category} &middot; {form.location || 'No location'}</div>
        <h5>₹{form.price || 0}/{form.priceUnit}</h5>
        <p>{form.description}</p>
        {(form.specifications || []).filter((s) => s.label || s.value).map((s, i) => (
          <div key={i} className="small"><span className="text-muted">{s.label}:</span> {s.value}</div>
        ))}
        <div className="small text-muted mt-2">{form.images.length} photo(s)</div>
      </Card.Body>
    </Card>
  );
}

export default function ItemForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  // The website owner (admin) reuses this exact form to edit any listing, then lands back on the
  // admin dashboard rather than "My Pieces".
  const isAdmin = user?.role === 'admin';
  const homePath = isAdmin ? '/admin' : '/owner';

  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(isEdit);
  const [loaded, setLoaded] = useState(!isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!isEdit) return;
    getItem(id)
      .then((item) => { setForm(toFormState(item)); setLoaded(true); })
      .catch(() => setError('Could not load this piece.'))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  // fix_issue3.md #6 - dynamic specification rows.
  const setSpec = (index, key) => (e) => setForm((prev) => ({
    ...prev,
    specifications: prev.specifications.map((s, i) => (i === index ? { ...s, [key]: e.target.value } : s))
  }));
  const addSpec = () => setForm((prev) => ({ ...prev, specifications: [...prev.specifications, { label: '', value: '' }] }));
  const removeSpec = (index) => setForm((prev) => ({ ...prev, specifications: prev.specifications.filter((_, i) => i !== index) }));

  const handleFilesSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // let the same file be re-selected later if removed then re-added
    if (files.length === 0) return;

    setUploadError('');
    setUploading(true);
    try {
      const urls = await uploadImages(files);
      setForm((prev) => ({ ...prev, images: [...prev.images, ...urls].slice(0, MAX_IMAGES) }));
    } catch (err) {
      setUploadError(err.response?.data?.msg || 'Unable to upload those images');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = (index) => {
    setForm((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setSubmitting(true);
    setError('');
    try {
      const payload = toPayload(form);
      const savedId = isEdit ? (await updateItem(id, payload), id) : (await createItem(payload))._id;
      // blockedDates has its own dedicated route (not part of the listing body).
      await saveBlockedDates(savedId, sanitizeBlockedDates(form.blockedDates)).catch(() => {});
      navigate(homePath); // hassle-free: land back on the dashboard instead of an empty confirmation page
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to save this piece');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Spinner animation="border" role="status" />;

  const photosStep = (
    <Form.Group className="mb-3">
      <Form.Label>Photos</Form.Label>
      {uploadError && <Alert variant="danger" className="py-2">{uploadError}</Alert>}
      <div className="d-flex flex-wrap gap-2 mb-2">
        {form.images.map((src, index) => (
          <div key={src} className="position-relative">
            <img
              src={imageUrl(src)}
              alt={`${form.name || 'Piece'} ${index + 1}`}
              style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 6 }}
            />
            <Button
              type="button"
              variant="danger"
              size="sm"
              className="position-absolute p-0 d-flex align-items-center justify-content-center"
              style={{ top: -6, right: -6, width: 22, height: 22, borderRadius: '50%', lineHeight: 1 }}
              onClick={() => handleRemoveImage(index)}
              aria-label={`Remove photo ${index + 1}`}
            >
              ✕
            </Button>
          </div>
        ))}
        {uploading && (
          <div className="d-flex align-items-center justify-content-center" style={{ width: 90, height: 90 }}>
            <Spinner animation="border" size="sm" role="status" />
          </div>
        )}
      </div>
      <Form.Control
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple
        disabled={uploading || form.images.length >= MAX_IMAGES}
        onChange={handleFilesSelected}
      />
      <Form.Text muted>
        {form.images.length >= MAX_IMAGES
          ? `Maximum of ${MAX_IMAGES} photos reached - remove one to add another.`
          : 'JPEG, PNG, WEBP, or GIF. Large photos are fine — they are resized and compressed automatically before upload.'}
      </Form.Text>
    </Form.Group>
  );

  const steps = [
    {
      key: 'basics',
      title: 'Basics',
      validate: (v) => (v.name.trim() ? null : { name: 'Name is required.' }),
      render: () => (
        <>
          <JewelleryMetalNotice />
          <Form.Group className="mb-2">
            <Form.Label>Name</Form.Label>
            <Form.Control required placeholder="e.g. Heritage Bridal Set" value={form.name} onChange={handleChange('name')} />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Description</Form.Label>
            <Form.Control as="textarea" rows={3} value={form.description} onChange={handleChange('description')} />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Category</Form.Label>
            <Form.Select value={form.category} onChange={handleChange('category')}>
              {JEWELLERY_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </Form.Select>
          </Form.Group>
        </>
      )
    },
    {
      key: 'pricing',
      title: 'Pricing & policies',
      validate: (v) => {
        if (v.price === '' || Number(v.price) < 0) return { price: 'Enter a rental price of 0 or more.' };
        if (v.stock === '' || Number(v.stock) < 1) return { stock: 'Enter how many pieces you have in stock (at least 1).' };
        return null;
      },
      render: () => (
        <>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label>Rental price</Form.Label>
                <Form.Control type="number" min={0} required value={form.price} onChange={handleChange('price')} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-2">
                <Form.Label>Per</Form.Label>
                <Form.Select value={form.priceUnit} onChange={handleChange('priceUnit')}>
                  <option value="week">Week</option>
                  <option value="day">Day</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              {/* v8 stock_and_availability - how many identical pieces this listing covers;
                  drives the date picker's available/booked check. */}
              <Form.Group className="mb-2">
                <Form.Label>Number of pieces in stock</Form.Label>
                <Form.Control type="number" min={1} required value={form.stock} onChange={handleChange('stock')} />
                <Form.Text muted>How many identical pieces of this design you have to rent out.</Form.Text>
              </Form.Group>
            </Col>
          </Row>
          {/* v6: policy flags + minimum up-front payment, unified onto the shared sub-form. */}
          <ListingExtraFields form={form} setForm={setForm} vertical="jewelry" section="policies" />
          {isEdit && (
            <>
              <BlockedDatesFields
                value={form.blockedDates}
                onChange={(blockedDates) => setForm((prev) => ({ ...prev, blockedDates }))}
              />
              <InventoryPanel itemId={id} />
            </>
          )}
        </>
      )
    },
    {
      key: 'specs',
      title: 'Details & specs',
      render: () => (
        <>
          {/* jewelry_rental/itemsOrProducts/ListANewPiece - structured, category-aware and fully
              optional spec sheet (appearance, size, set contents, weight, condition). */}
          <JewellerySpecFields form={form} setForm={setForm} />

          {/* fix_issue3.md #6 - free-form spec rows for anything the structured fields don't cover. */}
          <Form.Group className="mb-3">
            <Form.Label>Additional specifications</Form.Label>
            {form.specifications.map((spec, index) => (
              <Row key={index} className="g-2 mb-2">
                <Col xs={5}>
                  <Form.Control size="sm" placeholder="Label (e.g. Weight)" value={spec.label} onChange={setSpec(index, 'label')} />
                </Col>
                <Col xs={5}>
                  <Form.Control size="sm" placeholder="Value (e.g. 1 gram)" value={spec.value} onChange={setSpec(index, 'value')} />
                </Col>
                <Col xs={2}>
                  <Button size="sm" variant="outline-danger" onClick={() => removeSpec(index)} aria-label={`Remove specification ${index + 1}`}>✕</Button>
                </Col>
              </Row>
            ))}
            <Button size="sm" variant="outline-secondary" onClick={addSpec}>+ Add specification</Button>
          </Form.Group>
        </>
      )
    },
    {
      key: 'location',
      title: 'Location',
      validate: (v) => (v.location.trim() ? null : { location: 'Location is required.' }),
      render: () => (
        <Form.Group className="mb-3">
          <Form.Label>Location</Form.Label>
          <Form.Control required value={form.location} onChange={handleChange('location')} />
        </Form.Group>
      )
    },
    {
      key: 'photos',
      title: 'Photos & review',
      render: () => (
        <>
          {photosStep}
          <hr />
          <h6>Review</h6>
          <dl className="row small mb-0">
            <dt className="col-sm-3">Name</dt><dd className="col-sm-9">{form.name || '—'}</dd>
            <dt className="col-sm-3">Category</dt><dd className="col-sm-9">{form.category}</dd>
            <dt className="col-sm-3">Price</dt><dd className="col-sm-9">₹{form.price || 0}/{form.priceUnit}</dd>
            <dt className="col-sm-3">In stock</dt><dd className="col-sm-9">{form.stock || '—'}</dd>
            <dt className="col-sm-3">Location</dt><dd className="col-sm-9">{form.location || '—'}</dd>
            <dt className="col-sm-3">Photos</dt><dd className="col-sm-9">{form.images.length}</dd>
          </dl>
          <p className="text-muted small mt-2">Use “Preview” to see the listing card, then submit.</p>
        </>
      )
    }
  ];

  return (
    <Card>
      <Card.Body>
        <Card.Title as="h3" className="mb-3">
          {isEdit ? 'Edit piece' : 'List a new piece'}
        </Card.Title>
        <FormWizard
          steps={steps}
          values={form}
          onChange={setForm}
          onSubmit={handleSubmit}
          submitting={submitting}
          submitLabel={isEdit ? 'Save changes' : 'List piece'}
          error={error}
          startVisitedAll={isEdit && loaded}
          renderPreview={(v) => <ListingPreview form={v} />}
        />
      </Card.Body>
    </Card>
  );
}
